<template>
  <form class="af-card" @submit.prevent="doSubmit" autocomplete="new-password">
    <div class="p-3">
      <div class="is-size-4 has-text-grey-icon">
        <span v-if="!!customerFirstName">
          {{ "Szia " + customerFirstName + "!" }}
        </span>
        <span v-else> {{ "Szia!" }} </span>
      </div>
      <div class="is-size-3 has-text-weight-bold mt-1 mb-4">
        {{ "Lépj be a folytatáshoz!" }}
      </div>
      <div class="has-text-grey mb-5">
        {{
          "A megadott telefonszámodra SMS-ben érkezik majd a belépéshez szükséges egyedi azonosító."
        }}
      </div>

      <validation-observer ref="codeObserver">
        <BInputWithValidation
          :label="'SMS-ben kapott kód'"
          rules="required"
          label-position="on-border"
          autocomplete="new-password"
          :name="name"
          v-model="code"
        />

        <template v-if="firstLogin && regConfigsLoaded">
          <statements-acceptance
            :statements="regConfigs"
            layout-type="REG"
            read-required-validation-message="af:registration.message.validation.statement_read_required"
            ref="statementAcceptances"
            :is-public="true"
          />
        </template>
      </validation-observer>

      <b-field>
        <b-button
          type="is-blue"
          native-type="submit"
          expanded
          :loading="loading"
          >{{ $t("af:registration.confirmation.button.next") }}</b-button
        >
      </b-field>
      <p
        v-if="timer === 0"
        @click="reSendTwoFactor()"
        class="has-text-centered has-text-weight-bold is-link"
      >
        Megerősítő kód újraküldése
      </p>
      <p v-else class="has-text-centered has-text-grey">
        <b-icon icon-pack="fa" icon="info-circle"></b-icon>
        <span class="has-text-grey-icon is-size-6">{{
          timer + " másodperc múlva kérhetsz új kódot"
        }}</span>
      </p>
    </div>
  </form>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import i18n from "@/plugins/i18n";
import {
  clearLandingParams,
  getActiveLandingParams,
} from "../utils/landingParamUtil";
import twoFactorMixin from "../mixins/twoFactorMixin";

export default {
  name: "RegistrationConfirm",
  title: "af:page.registration_confirm.title",
  mixins: [twoFactorMixin],
  components: {
    BInputWithValidation: () => import("@/components/BInputWithValidation.vue"),
    StatementsAcceptance: () => import("@/components/StatementsAcceptance.vue"),
  },
  data() {
    return {
      code: "",
      secondFactorId: "",
      secondFactorNonce: "",
      name: (Math.random() + 1).toString(36).substring(5),
      firstLogin: false,
      userId: null,
      regConfigsLoaded: false,
      regConfigs: [],
      customerFirstName: null,
      timer: 29,
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      secondFactor: "session/secondFactor",
      loggedIn: "session/isReady",
    }),
  },
  async mounted() {
    if (this.loggedIn) {
      await this.$router.push("/user/profile");
    } else if (!this.secondFactor) {
      await this.$router.push("/login");
    } else {
      this.secondFactorId = this.secondFactor.id;
      this.secondFactorNonce = this.secondFactor.nonce;
      this.customerFirstName = this.secondFactor.customerFirstName;
      this.firstLogin = !!this.secondFactor.firstLogin;
      this.userId = this.secondFactor.userId;

      if (this.firstLogin) {
        await this.fetchRegConfig();
        this.regConfigs =
          this.$store.getters["acceptanceRequirements/regConfig"];
        this.regConfigs.forEach((rc) => {
          if (rc.promptDocument === "null") {
            rc.promptDocument = null;
          }
        });
        this.regConfigsLoaded = true;
      }

      this.startCountDown();
      await this.$store.dispatch("session/deleteSecondFactor");
    }
  },
  methods: {
    ...mapActions({
      fetchRegConfig: "acceptanceRequirements/fetchRegConfig",
    }),
    getAcceptedStatements() {
      if (!this.firstLogin) {
        return [];
      }
      let statements = [];

      let statementAcceptances = this.$refs.statementAcceptances;
      for (let rc in this.regConfigs) {
        if (
          statementAcceptances.statementValueBucket[
            this.regConfigs[rc].promptId
          ]
        ) {
          statements.push({
            statementId: this.regConfigs[rc].promptId,
            statementIdentifier: this.regConfigs[rc].promptIdentifier,
            documentId: this.regConfigs[rc].promptDocument,
            link: this.regConfigs[rc].link,
            statementLabel: this.regConfigs[rc].promptLabel,
            isRequired: this.regConfigs[rc].isRequired,
          });
        }
      }
      return statements;
    },

    async doSubmit() {
      try {
        const valid = await this.$refs.codeObserver.validate();
        if (!valid) return;
        this.loading = true;

        this.track();

        let statements = this.getAcceptedStatements();

        let registration = {
          id: this.secondFactorId,
          code: this.code,
          nonce: this.secondFactorNonce,
          acceptedStatements: statements,
          userId: this.userId,
          landingParams: getActiveLandingParams(),
        };

        await this.finishRegistration(registration);

        clearLandingParams();
        this.$router.push("/onboarding"); // after registration, this is always correct
      } catch (ex) {
        this.$buefy.toast.open({
          message: i18n.t("af:registration.error.general"),
          type: "is-danger",
        });
        console.error(ex);
      }

      this.loading = false;
    },
    track() {
      this.$gtag.event("register_2fa");
    },
  },
};
</script>

<style scoped lang="scss">
.af-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
}

@media only screen and (min-width: 769px) {
  .af-card {
    padding: 32px 36px;
  }
}

.is-link {
  color: #1072fc;
  cursor: pointer;
}
</style>
